"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Inquiries() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: closingRef,
    isVisible: closingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const faqs = [{
    question: "What is the process for starting services?",
    answer: "Our intake process begins with a consultation to understand your family's specific needs. Once we confirm availability and alignment with our neutral, child-centered approach, we complete the necessary paperwork, review court orders, and schedule your first session."
  }, {
    question: "How are payment and billing handled?",
    answer: "We maintain transparency in all financial matters. Fees are discussed during the intake process and are typically required in advance of scheduled sessions. We provide clear invoices and accept various payment methods to ensure a smooth, professional experience for all parties."
  }, {
    question: "What are the expectations for parents during visits?",
    answer: "We require all participants to adhere to our safety and conduct guidelines. Our role is to provide a neutral, structured environment; therefore, we expect respectful communication, adherence to the court-ordered schedule, and a primary focus on the child's well-being at all times."
  }, {
    question: "Do you provide reports for the court?",
    answer: "Yes, we provide objective, observation-based reporting for the court. Our documentation is neutral, trauma-informed, and focused on the interactions observed during supervised parenting time or exchanges, ensuring the court has accurate information to support the child's best interests."
  }, {
    question: "Are your services trauma-informed?",
    answer: "Absolutely. We recognize that court involvement and family separation can be stressful. Our staff is trained to provide a calm, steady, and supportive environment that minimizes further trauma for children and families while maintaining firm, professional boundaries."
  }, {
    question: "Can you accommodate high-conflict situations?",
    answer: "Yes, we specialize in high-conflict parenting matters. Our structured, neutral environment is designed specifically to manage these dynamics safely, ensuring that contact remains child-centered and that all safety protocols are strictly followed."
  }, {
    question: "What is your service area in Arizona?",
    answer: "Tiny to Teen Supervised Parenting & Education Services provides services throughout Arizona. We are a mobile, professional agency and can coordinate supervised parenting time, exchanges, and transportation in various locations. Please contact us to discuss your specific geographic needs."
  }, {
    question: "How do you ensure child safety?",
    answer: "Safety is our highest priority. We implement rigorous intake screening, maintain low ratios, and follow strict protocols for all interactions. Our staff is trained to intervene immediately if safety concerns arise, ensuring a secure environment for every child we serve."
  }];
  return <section data-section-id="4424" id="intakepaymentexpectationsfaq" className="bg-background text-foreground py-20 md:py-32 lg:py-40">
      <div className="container mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-32">
              <p className="font-default text-xs tracking-[0.25em] uppercase mb-8 text-secondary">
                Common Inquiries
              </p>
              <h2 className="font-default font-semibold text-foreground text-[2.5rem] md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight mb-8">
                Questions,{" "}
                <span className="italic font-default" style={{
                color: "var(--secondary)"
              }}>
                  answered.
                </span>
              </h2>
              <p className="font-default text-base md:text-lg leading-relaxed text-foreground/80 mb-10 max-w-md">
                We provide clarity on our intake process, expectations, and standards. If you have further questions, our team is here to assist you.
              </p>
              <Link to="/ContactPage" className="font-default text-sm text-foreground/70 hover:text-foreground transition-colors duration-300 inline-flex items-center gap-2 group">
                <span className="border-b border-foreground/30 group-hover:border-foreground pb-1 transition-colors duration-300">
                  Need more information? Contact us
                </span>
                <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">
                  &rarr;
                </span>
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group">
                  <AccordionTrigger className="font-default text-xl md:text-[1.375rem] font-medium text-foreground hover:no-underline py-7 text-left leading-snug data-[state=open]:text-secondary transition-colors duration-300 [&>svg]:hidden gap-6">
                    <span className="flex-1 pr-4">{faq.question}</span>
                    <span className="relative flex items-center justify-center size-6 shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-[135deg]">
                      <Plus className="size-5 text-foreground/60 group-data-[state=open]:text-secondary transition-colors duration-300" strokeWidth={1.25} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="font-default text-base text-foreground/80 leading-[1.7] pb-7 pr-12 max-w-2xl">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>

        {}
        <div ref={closingRef} className={`mt-24 md:mt-32 transition-all duration-1000 ease-out ${closingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="w-full h-px mb-10 bg-secondary" style={{
          opacity: 0.4
        }} />
          <p className="font-default italic text-center text-lg md:text-xl font-medium text-secondary">
            Committed to safety, neutrality, and the well-being of every child.
          </p>
        </div>
      </div>
    </section>;
}